import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Cartao, Selo, Vazio, LinhaDoTempo, ItemDaLinha } from "@notes/ui";
import { ListChecks, Clock, CheckCircle2, UserRound } from "lucide-react";
import { Pagina, Secao, Carregando, Erro } from "../shell/Shell.js";
import { usarPendencias, mensagemDeErro } from "../lib/api.js";
import { dataCurta } from "../lib/formato.js";

const FILTROS = [
  { chave: "todas", rotulo: "Todas" },
  { chave: "vencidas", rotulo: "Vencidas" },
  { chave: "minhas", rotulo: "Suas" },
  { chave: "cliente", rotulo: "Do cliente" },
] as const;

/**
 * PENDÊNCIAS. O que foi prometido e ainda não fechou, dos dois lados da mesa.
 *
 * Nenhum botão de "marcar como cumprida": a promessa fecha quando a PROVA
 * chega do CRM (proposta enviada, tarefa concluída, documento recebido). Um
 * botão aqui viraria o caminho por onde a lista fica limpa sem nada ter
 * acontecido com o cliente.
 */
export function Pendencias() {
  const q = usarPendencias();
  const [filtro, setFiltro] = useState<(typeof FILTROS)[number]["chave"]>("todas");

  if (q.isLoading) return <Pagina titulo="Pendências"><Carregando /></Pagina>;
  if (q.isError) {
    return <Pagina titulo="Pendências"><Erro mensagem={mensagemDeErro(q.error)} aoTentarDeNovo={() => void q.refetch()} /></Pagina>;
  }

  const hoje = new Date().toISOString().slice(0, 10);
  const todas = q.data?.promessas ?? [];
  const vencidas = todas.filter((p) => !!p.prazo && p.prazo < hoje);

  const lista = todas.filter((p) => {
    if (filtro === "vencidas") return !!p.prazo && p.prazo < hoje;
    if (filtro === "minhas") return p.quem === "consultor";
    if (filtro === "cliente") return p.quem === "cliente";
    return true;
  });

  return (
    <Pagina
      titulo="Pendências"
      descricao="O que ficou combinado nas conversas e ainda não fechou. Cada uma some sozinha quando a prova chega do CRM."
      acao={vencidas.length > 0 ? <Selo tom="critico">{vencidas.length} vencida{vencidas.length === 1 ? "" : "s"}</Selo> : undefined}
    >
      <Secao ordem={0}>
        <div className="enotes-chips" role="group" aria-label="Filtro">
          {FILTROS.map((f) => {
            const n = f.chave === "todas" ? todas.length
              : f.chave === "vencidas" ? vencidas.length
              : todas.filter((p) => p.quem === (f.chave === "minhas" ? "consultor" : "cliente")).length;
            return (
              <button key={f.chave} className="enotes-chip" data-ativo={filtro === f.chave || undefined}
                onClick={() => setFiltro(f.chave)}>
                {f.rotulo}<span className="num enotes-chip-num">{n}</span>
              </button>
            );
          })}
        </div>
      </Secao>

      <Secao ordem={1}>
        {lista.length === 0 ? (
          <Vazio
            titulo={todas.length === 0 ? "Nada em aberto" : "Nada com esse filtro"}
            descricao={todas.length === 0
              ? "Toda promessa feita nas conversas já foi cumprida. Quando uma conversa gerar um combinado, ele aparece aqui com o prazo."
              : "Tente outro filtro."}
          />
        ) : (
          <Cartao>
            <LinhaDoTempo>
              {lista.map((p) => {
                const atrasada = !!p.prazo && p.prazo < hoje;
                const minha = p.quem === "consultor";
                return (
                  <ItemDaLinha
                    key={p.id}
                    tom={atrasada ? "critico" : minha ? "atencao" : "info"}
                    icone={atrasada ? <Clock size={15} /> : minha ? <ListChecks size={15} /> : <UserRound size={15} />}
                    titulo={<><strong>{minha ? "Você" : "O cliente"}:</strong> {p.descricao}</>}
                    quando={p.prazo ? (atrasada ? `venceu em ${dataCurta(p.prazo)}` : dataCurta(p.prazo)) : "sem prazo"}
                    acao={p.reuniaoId
                      ? <Link to="/reunioes/$id" params={{ id: p.reuniaoId }} className="enotes-linha-link">ouvir a conversa</Link>
                      : undefined}
                  >
                    <p className="enotes-lt-nota">
                      <CheckCircle2 size={12} aria-hidden /> fecha sozinha quando {PROVA[p.provaEsperada] ?? "a prova chegar do CRM"}
                    </p>
                  </ItemDaLinha>
                );
              })}
            </LinhaDoTempo>
          </Cartao>
        )}
      </Secao>
    </Pagina>
  );
}

const PROVA: Record<string, string> = {
  proposta_enviada: "a proposta for marcada como enviada no CRM",
  tarefa_concluida: "a tarefa for concluída no CRM",
  documento_recebido: "o documento do cliente chegar",
};
